import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  Package, Truck, MapPin, DollarSign, Calendar, 
  CheckCircle, Send, Filter, Search, RefreshCw,
  ArrowRight, Clock, Users, Building2, Phone, Mail, Bell, Shield
} from 'lucide-react';
import { toast, Toaster } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://static.prod-images.emergentagent.com/jobs/0ee8c733-9ebe-4e41-9af6-e5aa20e6c9c1/images/664a6582c52f759cf8aa9c469d5205aa85a496bab31400c484f1532a982ea379.png";

const DriverLoadBoard = () => {
  const [loads, setLoads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    origin_state: '',
    dest_state: '',
    equipment: '',
    min_rate: ''
  });
  
  // MC Number verification
  const [mcNumber, setMcNumber] = useState('');
  const [mcVerified, setMcVerified] = useState(false);
  const [mcError, setMcError] = useState('');
  
  // Bid modal
  const [bidModalOpen, setBidModalOpen] = useState(false);
  const [selectedLoad, setSelectedLoad] = useState(null);
  const [bidSubmitting, setBidSubmitting] = useState(false);
  const [bidSuccess, setBidSuccess] = useState(false);
  
  const [bidForm, setBidForm] = useState({
    driver_name: '',
    driver_email: '',
    driver_phone: '',
    mc_number: '',
    equipment_type: 'flatbed',
    bid_amount: '',
    available_date: '',
    notes: ''
  });

  const verifyMcNumber = () => {
    if (!mcNumber || mcNumber.trim().length < 4) {
      setMcError('Please enter a valid MC number');
      return;
    }
    setMcVerified(true);
    setMcError('');
    setBidForm({ ...bidForm, mc_number: mcNumber });
    fetchLoads();
  };

  useEffect(() => {
    // Don't auto-fetch - require MC first
  }, []);

  const fetchLoads = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.origin_state) params.append('origin_state', filters.origin_state);
      if (filters.dest_state) params.append('dest_state', filters.dest_state);
      if (filters.equipment) params.append('equipment', filters.equipment);
      if (filters.min_rate) params.append('min_rate', filters.min_rate);
      
      const res = await axios.get(`${API}/marketplace/loads?${params.toString()}`);
      setLoads(res.data.loads || []);
    } catch (error) {
      console.error('Error fetching loads:', error);
      toast.error('Failed to fetch loads');
    } finally {
      setLoading(false);
    }
  };

  const openBidModal = (load) => {
    setSelectedLoad(load);
    setBidForm({ ...bidForm, bid_amount: load.rate || '' });
    setBidModalOpen(true);
    setBidSuccess(false);
  };

  const submitBid = async () => {
    if (!bidForm.driver_name || !bidForm.driver_email || !bidForm.driver_phone || !bidForm.bid_amount) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setBidSubmitting(true);
    try {
      const payload = {
        load_id: selectedLoad.id,
        ...bidForm,
        bid_amount: parseFloat(bidForm.bid_amount)
      };
      
      await axios.post(`${API}/marketplace/bid`, payload);
      setBidSuccess(true);
      toast.success('Bid submitted successfully!');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to submit bid');
    } finally {
      setBidSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Flexible';
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <header className="bg-slate-900/80 backdrop-blur-sm sticky top-0 z-50 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-3 md:px-4 py-3 md:py-4 flex items-center justify-between">
          <img src={LOGO_URL} alt="Tempus Dispatch" className="h-8 md:h-12" />
          <div className="flex items-center gap-2 md:gap-4">
            <a href="/alerts" className="text-slate-400 hover:text-white text-xs md:text-sm flex items-center gap-1">
              <Bell className="w-3 h-3 md:w-4 md:h-4" /> <span className="hidden sm:inline">Load Alerts</span>
            </a>
            <a href="/driver-signup" className="text-slate-400 hover:text-white text-xs md:text-sm hidden sm:block">Join Network</a>
            <a href="/post-load" className="bg-blue-600 hover:bg-blue-700 text-white px-2 md:px-4 py-1.5 md:py-2 rounded-lg text-xs md:text-sm font-bold">
              Post a Load
            </a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <div className="bg-gradient-to-r from-green-900/30 to-blue-900/30 py-6 md:py-10 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-2xl md:text-4xl font-bold text-white mb-2">Available Loads</h1>
          <p className="text-base md:text-xl text-slate-300">Find and bid on freight - No subscription required</p>
        </div>
      </div>

      {/* MC Number Verification Gate */}
      {!mcVerified ? (
        <div className="max-w-lg mx-auto px-4 py-12">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-6 md:p-8 text-center">
              <Shield className="w-16 h-16 text-blue-400 mx-auto mb-4" />
              <h2 className="text-xl md:text-2xl font-bold text-white mb-2">Enter Your MC Number</h2>
              <p className="text-slate-400 mb-6">To access our load board, please provide your Motor Carrier number.</p>
              
              <div className="space-y-4">
                <div>
                  <Input
                    value={mcNumber}
                    onChange={(e) => setMcNumber(e.target.value.toUpperCase())}
                    placeholder="MC-123456"
                    className="bg-slate-900 border-slate-600 text-center text-lg"
                    data-testid="mc-number-input"
                  />
                  {mcError && <p className="text-red-400 text-sm mt-2">{mcError}</p>}
                </div>
                
                <Button 
                  onClick={verifyMcNumber}
                  className="w-full bg-green-600 hover:bg-green-700 text-lg py-6"
                  data-testid="verify-mc-btn"
                >
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Access Load Board
                </Button>
              </div>
              
              <p className="text-slate-500 text-xs mt-6">
                Don't have an MC number? <a href="/driver-signup" className="text-blue-400 hover:underline">Join our carrier network</a>
              </p>
            </CardContent>
          </Card>
        </div>
      ) : (
      <>
      {/* Filters */}
      <div className="max-w-7xl mx-auto px-3 md:px-4 py-4 md:py-6">
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-green-400 text-sm flex items-center gap-1">
                <CheckCircle className="w-4 h-4" /> MC: {mcNumber}
              </span>
              <button 
                onClick={() => { setMcVerified(false); setMcNumber(''); setLoads([]); }}
                className="text-slate-400 hover:text-white text-xs"
              >
                Change MC
              </button>
            </div>
            <div className="grid grid-cols-2 md:flex md:flex-wrap gap-2 md:gap-4 items-end">
              <div>
                <Label className="text-slate-400 text-xs">Origin</Label>
                <Input
                  value={filters.origin_state}
                  onChange={(e) => setFilters({...filters, origin_state: e.target.value.toUpperCase()})}
                  placeholder="CA"
                  maxLength={2}
                  className="w-full md:w-20 bg-slate-900 border-slate-600"
                />
              </div>
              <div>
                <Label className="text-slate-400 text-xs">Dest</Label>
                <Input
                  value={filters.dest_state}
                  onChange={(e) => setFilters({...filters, dest_state: e.target.value.toUpperCase()})}
                  placeholder="TX"
                  maxLength={2}
                  className="w-full md:w-20 bg-slate-900 border-slate-600"
                />
              </div>
              <div>
                <Label className="text-slate-400 text-xs">Equipment</Label>
                <select
                  value={filters.equipment}
                  onChange={(e) => setFilters({...filters, equipment: e.target.value})}
                  className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-600 text-white text-sm"
                >
                  <option value="">All</option>
                  <option value="sprinter_van">Sprinter Van</option>
                  <option value="cargo_van">Cargo Van</option>
                  <option value="box_truck">Box Truck</option>
                  <option value="hotshot">Hotshot</option>
                  <option value="dry_van">Dry Van</option>
                  <option value="reefer">Reefer</option>
                  <option value="flatbed">Flatbed</option>
                  <option value="step_deck">Step Deck</option>
                  <option value="conestoga">Conestoga</option>
                  <option value="lowboy">Lowboy</option>
                  <option value="rgn">RGN</option>
                  <option value="double_drop">Double Drop</option>
                  <option value="curtain_side">Curtain Side</option>
                  <option value="tanker">Tanker</option>
                  <option value="hopper">Hopper</option>
                  <option value="dump_truck">Dump Truck</option>
                  <option value="power_only">Power Only</option>
                </select>
              </div>
              <div>
                <Label className="text-slate-400 text-xs">Min Rate</Label>
                <Input
                  type="number"
                  value={filters.min_rate}
                  onChange={(e) => setFilters({...filters, min_rate: e.target.value})}
                  placeholder="$0"
                  className="w-full md:w-24 bg-slate-900 border-slate-600"
                />
              </div>
              <div className="col-span-2 md:col-span-1 flex gap-2">
                <Button onClick={fetchLoads} className="flex-1 md:flex-none bg-blue-600 hover:bg-blue-700 text-sm">
                  <Search className="w-4 h-4 mr-1" /> Search
                </Button>
                <Button variant="outline" onClick={() => { setFilters({ origin_state: '', dest_state: '', equipment: '', min_rate: '' }); fetchLoads(); }} className="flex-1 md:flex-none text-sm">
                  <RefreshCw className="w-4 h-4 mr-1" /> Reset
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Load List */}
      <div className="max-w-7xl mx-auto px-3 md:px-4 pb-12">
        {loading ? (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
            <p className="text-slate-400 mt-4">Loading available loads...</p>
          </div>
        ) : loads.length === 0 ? (
          <div className="text-center py-12">
            <Package className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <h3 className="text-xl text-white mb-2">No loads found</h3>
            <p className="text-slate-400">Try adjusting your filters or check back later</p>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-slate-400 text-sm">{loads.length} loads available</p>
            
            {loads.map((load) => (
              <Card key={load.id} className="bg-slate-800/50 border-slate-700 hover:border-blue-500/50 transition-colors">
                <CardContent className="p-4 md:p-6">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    {/* Route Info */}
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 md:gap-3 mb-3">
                        <div className="flex items-center gap-1 md:gap-2 text-white font-bold text-base md:text-lg">
                          <MapPin className="w-4 h-4 md:w-5 md:h-5 text-green-400" />
                          {load.origin}
                        </div>
                        <ArrowRight className="w-4 h-4 md:w-5 md:h-5 text-slate-500" />
                        <div className="flex items-center gap-1 md:gap-2 text-white font-bold text-base md:text-lg">
                          <MapPin className="w-4 h-4 md:w-5 md:h-5 text-red-400" />
                          {load.destination}
                        </div>
                      </div>
                      
                      <div className="flex flex-wrap gap-2 md:gap-4 text-xs md:text-sm text-slate-400">
                        <span className="flex items-center gap-1">
                          <Truck className="w-3 h-3 md:w-4 md:h-4" />
                          {load.equipment || 'Flatbed'}
                        </span>
                        {load.weight && (
                          <span className="flex items-center gap-1">
                            <Package className="w-3 h-3 md:w-4 md:h-4" />
                            {load.weight.toLocaleString()} lbs
                          </span>
                        )}
                        {load.pickup_date && (
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3 md:w-4 md:h-4" />
                            {formatDate(load.pickup_date)}
                          </span>
                        )}
                        {load.company && (
                          <span className="flex items-center gap-1">
                            <Building2 className="w-3 h-3 md:w-4 md:h-4" />
                            {load.company}
                          </span>
                        )}
                        {load.bids_count > 0 && (
                          <span className="flex items-center gap-1 text-yellow-400">
                            <Users className="w-3 h-3 md:w-4 md:h-4" />
                            {load.bids_count} bid{load.bids_count > 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Rate & Bid */}
                    <div className="flex items-center justify-between md:flex-col md:text-right md:ml-6 pt-3 md:pt-0 border-t md:border-t-0 border-slate-700">
                      {load.rate ? (
                        <div className="md:mb-3">
                          <p className="text-2xl md:text-3xl font-bold text-green-400">${load.rate.toLocaleString()}</p>
                          <p className="text-xs text-slate-500">{load.rate_type === 'per_mile' ? 'per mile' : 'flat rate'}</p>
                        </div>
                      ) : (
                        <p className="text-lg md:text-xl text-yellow-400 md:mb-3">Open to Offers</p>
                      )}
                      
                      <Button 
                        onClick={() => openBidModal(load)}
                        className="bg-green-600 hover:bg-green-700 text-sm md:text-base"
                      >
                        <Send className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
                        Place Bid
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Bid Modal */}
      <Dialog open={bidModalOpen} onOpenChange={setBidModalOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">
              {bidSuccess ? '✅ Bid Submitted!' : 'Place Your Bid'}
            </DialogTitle>
          </DialogHeader>
          
          {bidSuccess ? (
            <div className="text-center py-6">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <p className="text-slate-300 mb-4">
                Your bid of <strong className="text-green-400">${parseFloat(bidForm.bid_amount).toLocaleString()}</strong> has been submitted!
              </p>
              <p className="text-slate-400 text-sm mb-6">
                The dispatcher will contact you at <strong>{bidForm.driver_email}</strong> if your bid is selected.
              </p>
              <Button onClick={() => { setBidModalOpen(false); setBidSuccess(false); }}>
                Close
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {selectedLoad && (
                <div className="p-4 bg-slate-900 rounded-lg">
                  <p className="text-white font-bold">{selectedLoad.origin} → {selectedLoad.destination}</p>
                  <p className="text-slate-400 text-sm">{selectedLoad.equipment} | {selectedLoad.rate ? `$${selectedLoad.rate.toLocaleString()}` : 'Open'}</p>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label className="text-slate-300">Your Name *</Label>
                  <Input
                    value={bidForm.driver_name}
                    onChange={(e) => setBidForm({...bidForm, driver_name: e.target.value})}
                    placeholder="John Smith"
                    className="bg-slate-900 border-slate-600"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Email *</Label>
                  <Input
                    type="email"
                    value={bidForm.driver_email}
                    onChange={(e) => setBidForm({...bidForm, driver_email: e.target.value})}
                    placeholder="john@email.com"
                    className="bg-slate-900 border-slate-600"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Phone *</Label>
                  <Input
                    value={bidForm.driver_phone}
                    onChange={(e) => setBidForm({...bidForm, driver_phone: e.target.value})}
                    placeholder="(555) 123-4567"
                    className="bg-slate-900 border-slate-600"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">MC Number</Label>
                  <Input
                    value={bidForm.mc_number}
                    onChange={(e) => setBidForm({...bidForm, mc_number: e.target.value})}
                    placeholder="MC123456"
                    className="bg-slate-900 border-slate-600"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Equipment</Label>
                  <select
                    value={bidForm.equipment_type}
                    onChange={(e) => setBidForm({...bidForm, equipment_type: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-600 text-white"
                  >
                    <option value="sprinter_van">Sprinter Van</option>
                    <option value="cargo_van">Cargo Van</option>
                    <option value="box_truck">Box Truck</option>
                    <option value="hotshot">Hotshot</option>
                    <option value="dry_van">Dry Van</option>
                    <option value="reefer">Reefer</option>
                    <option value="flatbed">Flatbed</option>
                    <option value="step_deck">Step Deck</option>
                    <option value="conestoga">Conestoga</option>
                    <option value="lowboy">Lowboy</option>
                    <option value="rgn">RGN</option>
                    <option value="double_drop">Double Drop</option>
                    <option value="curtain_side">Curtain Side</option>
                    <option value="tanker">Tanker</option>
                    <option value="hopper">Hopper</option>
                    <option value="dump_truck">Dump Truck</option>
                    <option value="power_only">Power Only</option>
                  </select>
                </div>
                <div>
                  <Label className="text-slate-300">Your Bid Amount *</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="number"
                      value={bidForm.bid_amount}
                      onChange={(e) => setBidForm({...bidForm, bid_amount: e.target.value})}
                      className="pl-9 bg-slate-900 border-slate-600 text-lg font-bold"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-slate-300">Available Date</Label>
                  <Input
                    type="date"
                    value={bidForm.available_date}
                    onChange={(e) => setBidForm({...bidForm, available_date: e.target.value})}
                    className="bg-slate-900 border-slate-600"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-slate-300">Notes (optional)</Label>
                  <Input
                    value={bidForm.notes}
                    onChange={(e) => setBidForm({...bidForm, notes: e.target.value})}
                    placeholder="Any additional info..."
                    className="bg-slate-900 border-slate-600"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setBidModalOpen(false)} className="flex-1">
                  Cancel
                </Button>
                <Button 
                  onClick={submitBid} 
                  disabled={bidSubmitting}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  {bidSubmitting ? 'Submitting...' : 'Submit Bid'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="bg-slate-900 border-t border-slate-700 py-8">
        <div className="max-w-7xl mx-auto px-4 text-center text-slate-500 text-sm">
          <p>© 2026 Tempus Dispatch LLC | The Freight Marketplace for Shippers, Brokers & Carriers</p>
          <p className="mt-2">733,000+ Carriers | 48 State Coverage | No Subscription Required</p>
        </div>
      </footer>
      </>
      )}
    </div>
  );
};

export default DriverLoadBoard;
